import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { forwardRef } from "react";
import { Link } from "react-router";

import { ShellPinnedMenu } from "@/vnext/app-shell/ShellPinnedMenu";
import {
  SHELL_PANEL_LABELS,
  type ShellPanelId,
  type useShellToolPanels,
} from "@/vnext/app-shell/ShellToolPanels";
import type { usePinnedPages } from "@/vnext/app-shell/usePinnedPages";
import { cx } from "@/vnext/shared/cx";

export interface ShellBreadcrumb {
  label: string;
  path?: string;
}

const shortcutKey = () =>
  typeof navigator !== "undefined" &&
  navigator.platform.toLowerCase().includes("mac")
    ? "⌘B"
    : "Ctrl+B";

/**
 * The one-row top bar (F-002, F-018 to F-020, F-199, F-204): the collapse
 * button and breadcrumb on the left; Pinned, Log issue and AI Assistant on
 * the right. The open panel's button keeps its open state (F-207).
 */
export const ShellTopBar = forwardRef<
  HTMLButtonElement,
  {
    breadcrumbs: ShellBreadcrumb[];
    navCollapsed: boolean;
    narrowNavOpen: boolean;
    onToggleCollapsed: () => void;
    onToggleNarrowNav: () => void;
    panelId: string;
    panels: ReturnType<typeof useShellToolPanels>;
    pins: ReturnType<typeof usePinnedPages>;
    sidebarId: string;
  }
>(function ShellTopBar(
  {
    breadcrumbs,
    navCollapsed,
    narrowNavOpen,
    onToggleCollapsed,
    onToggleNarrowNav,
    panelId,
    panels,
    pins,
    sidebarId,
  },
  narrowButtonRef,
) {
  const collapseLabel = `${navCollapsed ? "Expand menu" : "Collapse menu"} ${shortcutKey()}`;
  return (
    <header className="vnext-topbar">
      <div className="vnext-topbar__leading">
        <button
          aria-controls={sidebarId}
          aria-expanded={!navCollapsed}
          aria-label={navCollapsed ? "Expand menu" : "Collapse menu"}
          className="vnext-shell-collapse vnext-shell-collapse--desktop"
          onClick={onToggleCollapsed}
          title={collapseLabel}
          type="button"
        >
          {navCollapsed ? (
            <MenuOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
          ) : (
            <MenuOpenOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
          )}
        </button>
        <button
          aria-controls={sidebarId}
          aria-expanded={narrowNavOpen}
          aria-label={narrowNavOpen ? "Close navigation" : "Open navigation"}
          className="vnext-shell-collapse vnext-shell-collapse--narrow"
          onClick={onToggleNarrowNav}
          ref={narrowButtonRef}
          type="button"
        >
          <MenuOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
        </button>
        {breadcrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="vnext-breadcrumbs">
            {breadcrumbs.map((crumb, index) => {
              const isCurrent = index === breadcrumbs.length - 1;
              return (
                <span
                  className={cx(
                    "vnext-breadcrumbs__item",
                    isCurrent && "is-current",
                  )}
                  key={`${crumb.label}-${crumb.path ?? index}`}
                >
                  {crumb.path && !isCurrent ? (
                    <Link to={crumb.path}>{crumb.label}</Link>
                  ) : (
                    <span
                      aria-current={isCurrent ? "page" : undefined}
                      title={isCurrent ? crumb.label : undefined}
                    >
                      {crumb.label}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
        ) : null}
      </div>
      <div className="vnext-topbar__tools">
        <ShellPinnedMenu pins={pins} />
        {(["log_issue", "assistant"] as ShellPanelId[]).map((id) =>
          panels.available[id] ? (
            <button
              aria-controls={panels.active === id ? panelId : undefined}
              aria-expanded={panels.active === id}
              className={cx(
                "vnext-shell-tool",
                id === "assistant" && "vnext-shell-tool--assistant",
                panels.active === id && "is-open",
              )}
              key={id}
              onClick={() => panels.toggle(id)}
              ref={(element) => {
                panels.buttonRefs.current[id] = element;
              }}
              type="button"
            >
              {id === "assistant" ? (
                <AutoAwesomeOutlinedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14 }}
                />
              ) : (
                <BugReportOutlinedIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14 }}
                />
              )}
              {SHELL_PANEL_LABELS[id]}
            </button>
          ) : null,
        )}
      </div>
    </header>
  );
});
